import React, { useEffect, useState } from "react";
import {
  Dialog,
  Stack,
  Typography,
  Link,
  useTheme,
  IconButton,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { set, useForm } from "react-hook-form";
import { LoadingButton } from "@mui/lab";

import { useDialog, useRequestState, useResponsive } from "@app/hooks";
import { FormProvider, RHFTextField, useSnackbar } from "@app/components";
import { Strings } from "@app/constants";
import { useDispatch, useSelector } from "react-redux";
import { loginWithOtpSchema } from "../validators";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { login, resetLoginState } from "../slice";
import { selectLoginState } from "../selectors";
import { selectIsAuthenticated } from "@app/modules/common";
import { sendOtpUsingWidget } from "@app/components/msg91/msg91Otp";

declare global {
  interface Window {
    initSendOTP?: (config: any) => void;
    sendOtp?: (mobile: string, success: (res: any) => void, err: (e: any) => void) => void;
    verifyOtp?: any;
    retryOtp?: any;
  }
}

interface LoginDialogProps {
  open: boolean;
  onClose: () => void;
  onSignUpClick?: () => void;
  onClickOtpVerificationsDialog?: () => void;
  setLoginFormData?: (form: any) => void;
}

const LoginDialog = ({
  open,
  onClose,
  onSignUpClick,
  onClickOtpVerificationsDialog,
  setLoginFormData,
}: LoginDialogProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const methods = useForm<any>({
    resolver: yupResolver(loginWithOtpSchema),
  });
  const dispatch = useDispatch();
  const loginState = useSelector(selectLoginState);

  const { loading } = useRequestState({
    stateSelector: selectLoginState,
  })

  const [otpLoading, setOtpLoading] = useState(false);

  const textStyle = (size: number, weight = 400, color?: string) => ({
    fontFamily: "Work Sans",
    fontWeight: weight,
    fontSize: size,
    ...(color && { color }),
  });

  const FormField = ({
    name,
    label,
    placeholder,
  }: {
    name: string;
    label: string;
    placeholder: string;
  }) => (
    <Stack width={isMobile ? "100%" : "82%"}>
      <Typography sx={textStyle(14, 500, theme.palette.grey[700])} mb={0.5}>
        {label}
      </Typography>
      <RHFTextField
        name={name}
        placeholder={placeholder}
        variant="outlined"
        fullWidth
        sx={{
          "& .MuiOutlinedInput-root fieldset": { border: "none" },
          backgroundColor: theme.palette.grey[200],
          borderRadius: 1,
        }}
      />
    </Stack>
  );

  const onSubmit = (form: any) => {
    (async () => {
      try {
        setOtpLoading(true)
        const mobile = form.phoneNumber || form.phone || "";
        await sendOtpUsingWidget(mobile);
        enqueueSnackbar("OTP sent via widget!", { variant: "success" });
        setLoginFormData?.(form);
        onClickOtpVerificationsDialog?.();
        dispatch(login(form));
        setOtpLoading(false)
      } catch (err) {
        // widget not available or errored -> fallback to existing server-side flow
        dispatch(login(form));
        setOtpLoading(false)
      }
    })();
  };

  // MSG91 script is loaded at application root by `Msg91Provider`.
  // This component uses `window.sendOtp` if available; otherwise it falls back to
  // the existing `login` dispatch.



  useEffect(() => {
    if (loginState?.status === 2 && loginState?.data) {
      enqueueSnackbar("OTP sent successfully!", { variant: "success" });
      setLoginFormData?.(methods.getValues());
      onClickOtpVerificationsDialog?.();
    } else if (loginState?.status === 3) {
      enqueueSnackbar(loginState?.error?.message?.[0] || "Login failed", {
        variant: "error",
      });
    }
  }, [loginState]);

  const isAuthenticated = useSelector(selectIsAuthenticated);

  useEffect(() => {
    if (open) {
      dispatch(resetLoginState());
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (isAuthenticated) {
          onClose();
        }
      }}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : "55%",
          maxWidth: isMobile ? "100%" : "90%",
          borderRadius: isMobile ? 0 : 3,
          p: isMobile ? 2 : 0,
        },
      }}
    >
      <Stack
        sx={{
          maxHeight: "100vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack
          spacing={3}
          sx={{ position: "relative", p: isMobile ? 2 : 4, px: 4 }}
        >
          {/* <IconButton
            onClick={onClose}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Icon
              icon="material-symbols-light:close"
              width={24}
              height={24}
              color={theme.palette.common.black}
            />
          </IconButton> */}

          <Stack spacing={1} alignItems="center">
            <Typography sx={textStyle(isMobile ? 22 : 28, 700)}>
              {Strings.authPopup.logIn}
            </Typography>
            <Typography
              textAlign="center"
              sx={textStyle(isMobile ? 14 : 16, 400, theme.palette.grey[800])}
            >
              {Strings.authPopup.welcomeBackLogin}
            </Typography>
          </Stack>

          <FormProvider methods={methods}>
            <Stack spacing={2} alignItems="center">
              <FormField
                name="phoneNumber"
                label={`${Strings.authPopup.phoneNumber}*`}
                placeholder={Strings.authPopup.enterPhoneNumber}
              />
              <FormField
                name="email"
                label={Strings.authPopup.email}
                placeholder={Strings.authPopup.enterEmail}
              />

              <LoadingButton
                loading={otpLoading || loading}
                onClick={methods.handleSubmit(onSubmit)}
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: "8px",
                  width: isMobile ? "100%" : "65%",
                  py: 1.5,
                  mt: 2,
                }}
              >
                {Strings.button.continue}
              </LoadingButton>
            </Stack>
          </FormProvider>

          {onSignUpClick && (
            <Stack alignItems="center">
              <Typography
                sx={textStyle(isMobile ? 13 : 15, 500, theme.palette.grey[700])}
              >
                {Strings.auth.doNotHave}
                <Link
                  href="#"
                  underline="hover"
                  fontFamily="Work Sans"
                  fontWeight={500}
                  ml={1}
                  onClick={onSignUpClick}
                >
                  {Strings.auth.signUp}
                </Link>
              </Typography>
            </Stack>
          )}

          <Typography
            textAlign="center"
            sx={[
              textStyle(isMobile ? 11 : 13, 500, theme.palette.grey[700]),
              { px: 4 },
            ]}
          >
            {Strings.authPopup.byContinuingYou}
          </Typography>
        </Stack>
      </Stack>
    </Dialog>
  );
};

export { LoginDialog };
